import { getCurrentEntityPendingChanges } from 'app/pages/loanApplication/LoanApplication.utils';
import _ from 'lodash';

import {
  AddressField,
  AsianRaceSubType,
  BankruptType,
  CounselingFormat,
  DemographicInformationProvisionType,
  EthnicityType,
  HispanicOrLatinoEthnicitySubType,
  HomeBuyerCounseling,
  HousingType,
  LanguagePreference,
  MaritalStatusType,
  NativeHawaiianOrOtherRaceSubType,
  RaceType,
} from 'constants/loanApplication';
import { HistoryPendingChangesActionType } from 'constants/pendingChanges';
import { getConditionalObject } from 'utils/data';
import {
  checkBooleanPendingChangeVisibility,
  checkIsPendingChangeHasDifference,
  extractPendingChangesFromJson,
  fillPendingChangesWithActionType,
  formatDatePendingChange,
  formatPhoneNumberPendingChange,
  getApplicableAddressFieldsPendingChanges,
  getApplicableResidenceFieldsPendingChanges,
  mergePendingChanges,
  replaceNestedFieldsActionTypeToModified,
} from 'utils/pendingChanges';

import { previousAddressModifiedFieldsToExclude } from './Borrower/Address/Address.constants';
import { filterPreviousAddressForm } from './Borrower/Address/Address.utils';
import { SPOUSE_TYPE } from './MainContent.constants';

const initialPendingChangesPhoneFields = [
  'cellPhoneNumber',
  'workPhoneNumber',
  'homePhoneNumber',
  'poaPhone',
];

const initialPendingChangesFormattedKeys = {
  cellPhoneNumber: 'cellPhone',
  workPhoneNumber: 'workPhone',
  homePhoneNumber: 'homePhone',
  workPhoneExtension: 'workPhoneNumberExtension',
  numberOfDependents: 'dependents.count',
  ageOfDependents: 'dependents.agesOfDependents',
  socialSecurityNumber: 'ssn',
  poaName: 'powerOfAttorney.name',
  poaDesignation: 'powerOfAttorney.designation',
  poaVerbiage: 'powerOfAttorney.verbiage',
  poaEmail: 'powerOfAttorney.email',
  poaPhone: 'powerOfAttorney.phone',
  positiveRentalHistory: 'creditReporting.positiveRentalHistory',
  caivrsNumber: 'creditReporting.caivrsNumber',
  caivrsAuthorizationCode: 'creditReporting.caivrsAuthorizationCode',
  spouseType: 'relation',
  spouseOtherEmail: 'spouseEmail',
  spouseOtherFirstName: 'spouseFirstName',
  spouseOtherLastName: 'spouseLastName',
  isExemptVaFundingFee: 'isExemptVAFundingFee',
};

const declarationsPropertyAndLoanFields = [
  'intendToOccupyPropertyAsPrimaryResidence',
  'anyNewCreditOutsideOfTheLoan',
  'isPropertyLien',
  'loanOnAnotherProperty',
  'ownershipInterestPastThreeYears',
  'partOfDownPaymentBorrowed',
  'partOfDownPaymentBorrowedAmount',
  'pastThreeYearsPropertyPurpose',
  'relationshipWithPropertySeller',
  'titleHoldingType',
];

const declarationsFinancesFields = [
  'foreclosureSoldLessOutstandingBalanceWithinPastSevenYears',
  'guarantorOnAnyDebtOutsideTheLoan',
  'isLastMonthsBankruptcy',
  'isLastMonthsForeclosure',
  'outstandingJudgements',
  'partyToLawSuite',
  'presentlyObligatedOnAnyLoanOrDebt',
  'propertyInLieuOfForeclosureInPastSevenYears',
  'bankruptTypes',
];

const spouseInfoFields = [
  'spouseEmail',
  'spouseFirstName',
  'spouseLastName',
  'relation',
  'maritalStatus',
];

const declarationsMilitaryServiceFields = [
  'currentlyServingDate',
  'isCurrentlyRetired',
  'isCurrentlyServing',
  'isNonActivatedMemberOfReserveNationalGuard',
  'isServedInMilitary',
  'isSurvivingSpouse',
  'entitlementCodeFromCoe',
  'branchOfService',
  'militaryStatus',
  'basicEntitlement',
  'bonusEntitlement',
  'totalEntitlementAmount',
  'basicEntitlementManualUpdate',
  'bonusEntitlementManualUpdate',
  'firstTimeVaUse',
  'isExemptVAFundingFee',
];

const declarationsHomeownershipAndHousingFields = [
  'borrowerHomeownershipEducationFormat',
  'borrowerHousingCounselingFormat',
  'dateOfCompletionHomeownershipEducation',
  'dateOfCompletionHousingCounseling',
  'homeownershipEducationHousingCounselingAgencyAddress1',
  'homeownershipEducationHousingCounselingAgencyAddress2',
  'homeownershipEducationHousingCounselingAgencyCity',
  'homeownershipEducationHousingCounselingAgencyIdNumber',
  'homeownershipEducationHousingCounselingAgencyName',
  'homeownershipEducationHousingCounselingAgencyState',
  'homeownershipEducationHousingCounselingAgencyZip',
  'homeownershipEducationNameOfTheHousingEducationProgram',
  'housingCounselingAgencyAddress1',
  'housingCounselingAgencyAddress2',
  'housingCounselingAgencyCity',
  'housingCounselingAgencyIdNumber',
  'housingCounselingAgencyName',
  'housingCounselingAgencyState',
  'housingCounselingAgencyZip',
  'housingCounselingNameOfTheHousingEducationProgram',
  'isBorrowerCompletedHomeownershipEducation',
  'isBorrowerCompletedHousingCounseling',
];

const declarationLanguagePreferenceFields = ['languagePreference', 'otherLanguagePreference'];

const declarationHomeCounselingFields = [
  'counselingFormat',
  'counselingOtherDescription',
  'homeBuyerCounseling',
  'formatOtherDescription',
];

const hispanicOrLatinoFields = Object.values(HispanicOrLatinoEthnicitySubType);

const ethnicityTypeFields = [
  ...Object.values(EthnicityType),
  'isEthnicityNotProvidedDuringFaceToFaceInterview',
  'otherHispanicOrLatinoEthnicity',
];

const asianFields = Object.values(AsianRaceSubType);

const nativeHawaiianOrOtherPacificIslanderFields = Object.values(NativeHawaiianOrOtherRaceSubType);

const raceTypeFields = [
  ...Object.values(RaceType),
  'isRaceNotProvidedDuringFaceToFaceInterview',
  'otherPacificIslander',
  'otherAsianRace',
  'americanTribeName',
];

const demographicInformationFields = [
  'sexCollectedOnTheBasisOfVisualObservationOrSurname',
  'raceCollectedOnTheBasisOfVisualObservationOrSurname',
  'ethnicityCollectedOnTheBasisOfVisualObservationOrSurname',
  'demographicInformationProvisionType',
  'isSexInfoNotProvidedDuringFaceToFaceInterview',
  'sex',
];

const extractDemographicOptions = (fields, pendingChanges) =>
  fields.reduce((acc, fieldName) => {
    const newValue = pendingChanges[fieldName]?.newValue;
    const initialValue = pendingChanges[fieldName]?.initialValue;

    _.set(acc, `newValue[${fieldName}]`, _.isNull(newValue) ? false : newValue);
    _.set(acc, `initialValue[${fieldName}]`, _.isNull(initialValue) ? false : initialValue);

    return acc;
  }, {});

const parseStringedArrayPendingChangeValue = pendingChangeValue =>
  pendingChangeValue?.split(',') ?? [];

const normalizeInitialPendingChanges = ({
  pendingChanges,
  isBorrowerFlow,
  subjectPropertyAddressId,
}) =>
  Object.entries(pendingChanges).reduce((acc, [borrowerId, borrowerPendingChanges]) => {
    const initialValues = {
      declarations: {
        demographicInformation: {
          ethnicity: {
            hispanicOrLatinoOptions: extractDemographicOptions(
              hispanicOrLatinoFields,
              pendingChanges[borrowerId],
            ),
          },
          race: {
            asianOptions: extractDemographicOptions(asianFields, pendingChanges[borrowerId]),
            nativeHawaiianOrOtherPacificIslanderOptions: extractDemographicOptions(
              nativeHawaiianOrOtherPacificIslanderFields,
              pendingChanges[borrowerId],
            ),
          },
        },
      },
    };

    const formattedBorrowerPendingChanges = Object.entries(borrowerPendingChanges).reduce(
      (acc, [key, value]) => {
        const formattedKey = initialPendingChangesFormattedKeys[key] ?? key;

        switch (true) {
          case initialPendingChangesPhoneFields.includes(key):
            _.set(acc, formattedKey, formatPhoneNumberPendingChange(value));
            break;

          case formattedKey === 'dateOfBirth':
            _.set(acc, formattedKey, formatDatePendingChange(value));
            break;

          case spouseInfoFields.includes(formattedKey): {
            _.set(acc, formattedKey, {
              ...value,
              actionType: HistoryPendingChangesActionType.MODIFIED,
            });
            break;
          }

          case formattedKey === 'address' || formattedKey === 'subjectProperty':
            {
              const { startOfResidence, addressId, ...restCurrentResidencePendingChanges } = value;

              const formattedCurrentResidencePendingChanges = {
                ...restCurrentResidencePendingChanges,
                ...getConditionalObject(startOfResidence, () => ({
                  startOfResidence: formatDatePendingChange(startOfResidence),
                })),
                ...getConditionalObject(addressId, {
                  isSubjectProperty: {
                    newValue: addressId?.newValue === subjectPropertyAddressId,
                    initialValue: addressId?.initialValue === subjectPropertyAddressId,
                  },
                }),
              };

              const pathToSet =
                formattedKey === 'subjectProperty' || !isBorrowerFlow
                  ? formattedKey
                  : 'currentResidence';

              _.set(acc, pathToSet, formattedCurrentResidencePendingChanges);
            }

            break;

          case formattedKey === 'previousAddresses':
            {
              const formattedPrevAddresses = Object.entries(value).reduce(
                (acc, [addressId, addressPendingChanges]) => {
                  const { startOfResidence, endOfResidence, ...restPendingChanges } =
                    addressPendingChanges;

                  acc[addressId] = {
                    ...restPendingChanges,
                    ...getConditionalObject(startOfResidence, () => ({
                      startOfResidence: formatDatePendingChange(startOfResidence),
                    })),
                    ...getConditionalObject(endOfResidence, () => ({
                      endOfResidence: formatDatePendingChange(endOfResidence),
                    })),
                  };

                  return acc;
                },
                {},
              );

              _.set(acc, formattedKey, formattedPrevAddresses);
            }

            break;

          case formattedKey === 'dependents.count':
            {
              const formattedNumberOfDependents = {
                ...value,
                newValue: Number(value.newValue),
                initialValue: Number(value.initialValue),
              };

              _.set(acc, formattedKey, formattedNumberOfDependents);
            }

            break;

          case formattedKey === 'alternateNames':
            {
              const formattedAlternateNames = extractPendingChangesFromJson(value);

              _.set(acc, formattedKey, formattedAlternateNames);
            }

            break;

          case formattedKey === 'dependents.agesOfDependents':
            {
              const { newValue, initialValue } = value;

              const agesOfDependentsNewValue = parseStringedArrayPendingChangeValue(newValue);
              const agesOfDependentsInitialValue =
                parseStringedArrayPendingChangeValue(initialValue);

              const largerAgesOfDependents =
                agesOfDependentsNewValue.length >= agesOfDependentsInitialValue.length
                  ? agesOfDependentsNewValue
                  : agesOfDependentsInitialValue;

              const formattedAgesOfDependentsPendingChanges = largerAgesOfDependents.reduce(
                (acc, _, index) => {
                  acc[`agesOfDependents${index}`] = {
                    newValue: agesOfDependentsNewValue[index] ?? null,
                    initialValue: agesOfDependentsInitialValue[index] ?? null,
                  };

                  return acc;
                },
                {},
              );

              _.set(acc, formattedKey, formattedAgesOfDependentsPendingChanges);
            }

            break;

          case declarationsPropertyAndLoanFields.includes(formattedKey):
            _.set(acc, `declarations.propertyAndLoan[${formattedKey}]`, value);
            break;

          case declarationsFinancesFields.includes(formattedKey):
            {
              const formattedPath = `declarations.finances[${formattedKey}]`;

              if (formattedKey === 'bankruptTypes') {
                const formatBankruptTypes = bankruptTypes => {
                  const selectedBankruptTypes = parseStringedArrayPendingChangeValue(bankruptTypes);

                  return Object.values(BankruptType).reduce((acc, bankruptType) => {
                    acc[bankruptType] = selectedBankruptTypes.includes(bankruptType);

                    return acc;
                  }, {});
                };

                const formattedValue = {
                  ...value,
                  newValue: formatBankruptTypes(value.newValue),
                  initialValue: formatBankruptTypes(value.initialValue),
                };

                _.set(acc, formattedPath, formattedValue);

                break;
              }

              _.set(acc, formattedPath, value);
            }

            break;

          case declarationsMilitaryServiceFields.includes(formattedKey):
            _.set(acc, `declarations.militaryService[${formattedKey}]`, value);
            break;

          case declarationsHomeownershipAndHousingFields.includes(formattedKey):
            _.set(
              acc,
              `declarations.homeownershipEducationAndHousingCounseling[${formattedKey}]`,
              value,
            );
            break;

          case declarationLanguagePreferenceFields.includes(formattedKey):
            _.set(acc, `declarations.languagePreference[${formattedKey}]`, value);
            break;

          case declarationHomeCounselingFields.includes(formattedKey):
            _.set(acc, `declarations.homeCounseling[${formattedKey}]`, value);
            break;

          case demographicInformationFields.includes(formattedKey):
            _.set(acc, `declarations.demographicInformation[${formattedKey}]`, value);
            break;

          case ethnicityTypeFields.includes(formattedKey):
            {
              const formattedPendingChange = {
                newValue: value.newValue ?? false,
                initialValue: value.initialValue ?? false,
              };

              _.set(
                acc,
                `declarations.demographicInformation.ethnicity[${formattedKey}]`,
                formattedPendingChange,
              );
            }

            break;

          case raceTypeFields.includes(formattedKey):
            {
              const formattedPendingChange = {
                newValue: value.newValue ?? false,
                initialValue: value.initialValue ?? false,
              };

              _.set(
                acc,
                `declarations.demographicInformation.race[${formattedKey}]`,
                formattedPendingChange,
              );
            }

            break;

          case hispanicOrLatinoFields.includes(formattedKey):
          case asianFields.includes(formattedKey):
          case nativeHawaiianOrOtherPacificIslanderFields.includes(formattedKey):
            break;

          case formattedKey === 'mailingAddressId':
            _.set(
              acc,
              `isMailingAddressTheSameAsCurrentAddress`,
              _.mapValues(value, val => !val),
            );
            break;

          case formattedKey === 'creditReporting.positiveRentalHistory':
            _.set(
              acc,
              formattedKey,
              _.isNull(value.initialValue) ? { ...value, initialValue: true } : value,
            );
            break;

          default:
            _.set(acc, formattedKey, value);
        }

        return acc;
      },
      initialValues,
    );

    acc[borrowerId] = formattedBorrowerPendingChanges;

    return acc;
  }, {});

const filterPendingChanges = ({
  pendingChanges,
  passedBorrowerData = {},
  isFirstRun = false,
  isPositiveRentalHistoryVisible,
  isCaivrsNumberVisible,
}) =>
  Object.entries(pendingChanges).reduce((acc, [borrowerId, borrowerPendingChanges]) => {
    if (['parentPendingChanges', 'borrowerData'].includes(borrowerId)) {
      return acc;
    }

    const borrowerData = isFirstRun ? passedBorrowerData[borrowerId] : passedBorrowerData;

    // pending changes taken from the same nesting level
    const { isChangedFieldsMap, newValuesMap } = [
      'ownershipInterestPastThreeYears',
      'partOfDownPaymentBorrowed',
      'isLastMonthsBankruptcy',
      'isBorrowerCompletedHomeownershipEducation',
      'isBorrowerCompletedHousingCounseling',
      'languagePreference',
      'homeBuyerCounseling',
      'counselingFormat',
      'isHispanicOrLatino',
      'hispanicOrLatinoOptions',
      'demographicInformationProvisionType',
      'isAsian',
      'asianOptions',
      'isNativeHawaiianOrOtherPacificIslander',
      'nativeHawaiianOrOtherPacificIslanderOptions',
      'isAmericanIndianOrAlaskaNative',
      'housingType',
      'hasRelativesPartRent',
      'isMailingAddressTheSameAsCurrentAddress',
      'isServedInMilitary',
    ].reduce(
      (acc, fieldName) => {
        const pending = borrowerPendingChanges[fieldName];

        acc.isChangedFieldsMap[fieldName] = pending !== undefined;
        acc.newValuesMap[fieldName] = pending?.newValue;

        return acc;
      },
      {
        isChangedFieldsMap: {},
        newValuesMap: {},
      },
    );

    const filteredPendingChanges = Object.entries(borrowerPendingChanges).reduce(
      (filteredPendingChangesAcc, [pendingChangeKey, pendingChangeValue]) => {
        const getCondition = () => {
          switch (pendingChangeKey) {
            case 'dependents':
              {
                const agesOfDependentsPendingChangeValue = pendingChangeValue.agesOfDependents;

                const formattedPendingChangeValue = {
                  ...pendingChangeValue,
                  ...getConditionalObject(agesOfDependentsPendingChangeValue, () => ({
                    agesOfDependents: Object.fromEntries(
                      Object.entries(agesOfDependentsPendingChangeValue ?? {}).slice(
                        0,
                        pendingChangeValue.count?.newValue ?? Infinity,
                      ),
                    ),
                  })),
                };

                filteredPendingChangesAcc[pendingChangeKey] = formattedPendingChangeValue;
              }

              return false;

            case 'relation':
              return (
                !borrowerPendingChanges.maritalStatus ||
                borrowerPendingChanges.maritalStatus?.newValue === MaritalStatusType.MARRIED
              );

            case 'spouseEmail':
            case 'spouseFirstName':
            case 'spouseLastName':
              return (
                (!borrowerPendingChanges.maritalStatus ||
                  borrowerPendingChanges.maritalStatus?.newValue === MaritalStatusType.MARRIED) &&
                (!borrowerPendingChanges.relation ||
                  borrowerPendingChanges.relation?.newValue === SPOUSE_TYPE.OTHER)
              );

            case 'declarations':
            case 'propertyAndLoan':
            case 'finances':
            case 'militaryService':
            case 'homeownershipEducationAndHousingCounseling':
            case 'homeCounseling':
            case 'demographicInformation':
            case 'address':
            case 'creditReporting':
              filteredPendingChangesAcc[pendingChangeKey] = filterPendingChanges({
                pendingChanges: {
                  [pendingChangeKey]: pendingChangeValue,
                },
                passedBorrowerData: borrowerData,
                isPositiveRentalHistoryVisible,
                isCaivrsNumberVisible,
              })[pendingChangeKey];

              return false;

            case 'currentResidence':
              {
                const isSubjectPropertyPendingChange = pendingChangeValue.isSubjectProperty;
                const isSubjectPropertySavedValue =
                  borrowerData?.currentResidence?.isSubjectProperty;

                const getFormattedPendingChangeValue = () => {
                  if (
                    checkIsPendingChangeHasDifference(isSubjectPropertyPendingChange ?? {}) ||
                    checkBooleanPendingChangeVisibility({
                      newValue: isSubjectPropertyPendingChange?.newValue,
                      initialValue: isSubjectPropertySavedValue,
                    })
                  ) {
                    if (isSubjectPropertyPendingChange?.newValue) {
                      return {
                        ...borrowerPendingChanges.subjectProperty,
                        startOfResidence: pendingChangeValue?.startOfResidence,
                      };
                    }

                    return Object.values(AddressField).reduce(
                      (acc, fieldName) => {
                        acc[fieldName] = {
                          newValue: pendingChangeValue[fieldName]?.newValue,
                          initialValue: null,
                        };

                        return acc;
                      },
                      {
                        ...pendingChangeValue,
                      },
                    );
                  }

                  return pendingChangeValue;
                };

                const formattedPendingChangeValue = getFormattedPendingChangeValue();

                filteredPendingChangesAcc[pendingChangeKey] = filterPendingChanges({
                  pendingChanges: {
                    [pendingChangeKey]: replaceNestedFieldsActionTypeToModified({
                      isSubjectProperty: isSubjectPropertyPendingChange,
                      ...getApplicableAddressFieldsPendingChanges(formattedPendingChangeValue),
                      ...getApplicableResidenceFieldsPendingChanges(formattedPendingChangeValue),
                    }),
                  },
                  passedBorrowerData: borrowerData,
                })[pendingChangeKey];
              }

              return false;

            case 'mailingAddress':
              if (
                !isChangedFieldsMap.isMailingAddressTheSameAsCurrentAddress ||
                !!newValuesMap.isMailingAddressTheSameAsCurrentAddress
              ) {
                return false;
              }

              filteredPendingChangesAcc[pendingChangeKey] = filterPendingChanges({
                pendingChanges: {
                  [pendingChangeKey]: replaceNestedFieldsActionTypeToModified(pendingChangeValue),
                },
                passedBorrowerData: borrowerData,
              })[pendingChangeKey];

              return false;

            case 'ethnicity':
            case 'race':
              filteredPendingChangesAcc[pendingChangeKey] = filterPendingChanges({
                pendingChanges: {
                  [pendingChangeKey]: pendingChangeValue,
                  parentPendingChanges: borrowerPendingChanges,
                },
                passedBorrowerData: borrowerData,
              })[pendingChangeKey];

              return false;

            case 'languagePreference':
              if ('newValue' in pendingChangeValue) {
                return true;
              }

              filteredPendingChangesAcc[pendingChangeKey] = filterPendingChanges({
                pendingChanges: {
                  [pendingChangeKey]: pendingChangeValue,
                },
                passedBorrowerData: borrowerData,
              })[pendingChangeKey];

              return false;

            case 'previousAddresses':
              {
                const previousAddressesPendingChanges = Object.entries(pendingChangeValue).reduce(
                  (acc, [prevAddressId, prevAddressPendingChanges]) => {
                    acc[prevAddressId] = filterPendingChanges({
                      pendingChanges: {
                        [prevAddressId]: prevAddressPendingChanges,
                      },
                      passedBorrowerData: borrowerData,
                    })[prevAddressId];

                    return acc;
                  },
                  {},
                );

                filteredPendingChangesAcc[pendingChangeKey] = previousAddressesPendingChanges;
              }

              return false;

            case 'pastThreeYearsPropertyPurpose':
            case 'titleHoldingType':
              return (
                !isChangedFieldsMap.ownershipInterestPastThreeYears ||
                !!newValuesMap.ownershipInterestPastThreeYears
              );

            case 'partOfDownPaymentBorrowedAmount':
              return (
                !isChangedFieldsMap.partOfDownPaymentBorrowed ||
                !!newValuesMap.partOfDownPaymentBorrowed
              );

            case 'bankruptTypes':
              return (
                !isChangedFieldsMap.isLastMonthsBankruptcy || !!newValuesMap.isLastMonthsBankruptcy
              );

            case 'isCurrentlyRetired':
            case 'isCurrentlyServing':
            case 'isNonActivatedMemberOfReserveNationalGuard':
            case 'isSurvivingSpouse':
            case 'basicEntitlementManualUpdate':
            case 'bonusEntitlementManualUpdate':
              return (
                (!isChangedFieldsMap.isServedInMilitary || !!newValuesMap.isServedInMilitary) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'isHispanicOrLatino':
            case 'isNonHispanicOrLatino':
            case 'isMailingAddressTheSameAsCurrentAddress':
            case 'isOutsideUsa':
            case 'isLiveTogetherForTwoYears':
            case 'isSubjectProperty':
            case 'isFirstTimeHomeBuyer':
              return checkBooleanPendingChangeVisibility(pendingChangeValue);

            case 'currentlyServingDate':
            case 'entitlementCodeFromCoe':
            case 'branchOfService':
            case 'militaryStatus':
            case 'firstTimeVaUse':
            case 'isExemptVAFundingFee':
              return !isChangedFieldsMap.isServedInMilitary || !!newValuesMap.isServedInMilitary;

            case 'basicEntitlement':
            case 'bonusEntitlement':
            case 'totalEntitlementAmount':
              return (
                (!isChangedFieldsMap.isServedInMilitary || !!newValuesMap.isServedInMilitary) &&
                (Number(pendingChangeValue?.newValue) || 0) !==
                  (Number(pendingChangeValue?.initialValue) || 0)
              );

            case 'positiveRentalHistory':
              return (
                isPositiveRentalHistoryVisible &&
                (pendingChangeValue.initialValue === '' ||
                  checkBooleanPendingChangeVisibility(pendingChangeValue))
              );

            case 'caivrsNumber':
              return isCaivrsNumberVisible;

            case 'isSexInfoNotProvidedDuringFaceToFaceInterview':
              return (
                (!isChangedFieldsMap.demographicInformationProvisionType ||
                  newValuesMap.demographicInformationProvisionType ===
                    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'isRaceNotProvidedDuringFaceToFaceInterview':

            case 'isEthnicityNotProvidedDuringFaceToFaceInterview': {
              const isDemographicInformationProvisionTypeChanged =
                pendingChanges.parentPendingChanges?.demographicInformationProvisionType !==
                undefined;

              const demographicInformationProvisionTypeNewValue =
                pendingChanges.parentPendingChanges?.demographicInformationProvisionType?.newValue;

              return (
                (!isDemographicInformationProvisionTypeChanged ||
                  demographicInformationProvisionTypeNewValue ===
                    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );
            }

            case 'hispanicOrLatinoOptions':
              return !isChangedFieldsMap.isHispanicOrLatino || !!newValuesMap.isHispanicOrLatino;

            case 'otherHispanicOrLatinoEthnicity':
              return (
                (!isChangedFieldsMap.isHispanicOrLatino || !!newValuesMap.isHispanicOrLatino) &&
                (!isChangedFieldsMap.hispanicOrLatinoOptions ||
                  [true, undefined].includes(
                    newValuesMap.hispanicOrLatinoOptions?.[
                      HispanicOrLatinoEthnicitySubType.OTHER_HISPANIC_OR_LATINO
                    ],
                  ))
              );

            case 'asianOptions':
              return !isChangedFieldsMap.isAsian || !!newValuesMap.isAsian;

            case 'otherAsianRace':
              return (
                (!isChangedFieldsMap.isAsian || !!newValuesMap.isAsian) &&
                (!isChangedFieldsMap.asianOptions ||
                  [true, undefined].includes(
                    newValuesMap.asianOptions?.[AsianRaceSubType.OTHER_ASIAN],
                  ))
              );

            case 'nativeHawaiianOrOtherPacificIslanderOptions':
              return (
                !isChangedFieldsMap.isNativeHawaiianOrOtherPacificIslander ||
                !!newValuesMap.isNativeHawaiianOrOtherPacificIslander
              );

            case 'otherPacificIslander':
              return (
                (!isChangedFieldsMap.isNativeHawaiianOrOtherPacificIslander ||
                  !!newValuesMap.isNativeHawaiianOrOtherPacificIslander) &&
                (!isChangedFieldsMap.nativeHawaiianOrOtherPacificIslanderOptions ||
                  [true, undefined].includes(
                    newValuesMap.nativeHawaiianOrOtherPacificIslanderOptions?.[
                      NativeHawaiianOrOtherRaceSubType.OTHER_PACIFIC_ISLANDER
                    ],
                  ))
              );

            case 'americanTribeName':
              return (
                !isChangedFieldsMap.isAmericanIndianOrAlaskaNative ||
                !!newValuesMap.isAmericanIndianOrAlaskaNative
              );

            case 'sex':
              {
                const savedSexValue = borrowerData?.declarations?.demographicInformation?.rawSex;

                const valueToMerge = {
                  newValue: pendingChangeValue.newValue ?? savedSexValue,
                  initialValue: _.isUndefined(pendingChangeValue.initialValue)
                    ? savedSexValue
                    : pendingChangeValue.initialValue,
                };

                filteredPendingChangesAcc[pendingChangeKey] = _.merge(
                  pendingChangeValue,
                  valueToMerge,
                );
              }

              return false;

            case 'isEthnicityNotProvided':

            case 'isRaceNotProvided': {
              const isDemographicInformationProvisionTypeChanged =
                pendingChanges.parentPendingChanges?.demographicInformationProvisionType !==
                undefined;

              const demographicInformationProvisionTypeNewValue =
                pendingChanges.parentPendingChanges?.demographicInformationProvisionType?.newValue;

              return (
                (!isDemographicInformationProvisionTypeChanged ||
                  demographicInformationProvisionTypeNewValue !==
                    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );
            }

            case 'sexCollectedOnTheBasisOfVisualObservationOrSurname':
            case 'raceCollectedOnTheBasisOfVisualObservationOrSurname':
            case 'ethnicityCollectedOnTheBasisOfVisualObservationOrSurname':
              return (
                (!isChangedFieldsMap.demographicInformationProvisionType ||
                  newValuesMap.demographicInformationProvisionType ===
                    DemographicInformationProvisionType.FACE_TO_FACE_INTERVIEW) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'dateOfCompletionHomeownershipEducation':
            case 'homeownershipEducationNameOfTheHousingEducationProgram':
            case 'borrowerHomeownershipEducationFormat':
            case 'homeownershipEducationHousingCounselingAgencyAddress1':
            case 'homeownershipEducationHousingCounselingAgencyAddress2':
            case 'homeownershipEducationHousingCounselingAgencyCity':
            case 'homeownershipEducationHousingCounselingAgencyIdNumber':
            case 'homeownershipEducationHousingCounselingAgencyName':
            case 'homeownershipEducationHousingCounselingAgencyState':
            case 'homeownershipEducationHousingCounselingAgencyZip':
              return (
                !isChangedFieldsMap.isBorrowerCompletedHomeownershipEducation ||
                !!newValuesMap.isBorrowerCompletedHomeownershipEducation
              );

            case 'dateOfCompletionHousingCounseling':
            case 'housingCounselingNameOfTheHousingEducationProgram':
            case 'borrowerHousingCounselingFormat':
            case 'housingCounselingAgencyAddress1':
            case 'housingCounselingAgencyAddress2':
            case 'housingCounselingAgencyCity':
            case 'housingCounselingAgencyIdNumber':
            case 'housingCounselingAgencyName':
            case 'housingCounselingAgencyState':
            case 'housingCounselingAgencyZip':
              return (
                !isChangedFieldsMap.isBorrowerCompletedHousingCounseling ||
                !!newValuesMap.isBorrowerCompletedHousingCounseling
              );

            case 'otherLanguagePreference':
              return (
                !isChangedFieldsMap.languagePreference ||
                newValuesMap.languagePreference === LanguagePreference.OTHER
              );

            case 'counselingOtherDescription':
              return (
                !isChangedFieldsMap.homeBuyerCounseling ||
                newValuesMap.homeBuyerCounseling === HomeBuyerCounseling.OTHER
              );

            case 'formatOtherDescription':
              return (
                !isChangedFieldsMap.counselingFormat ||
                newValuesMap.counselingFormat === CounselingFormat.OTHER
              );

            case 'monthlyRentAmount':
              return (
                !isChangedFieldsMap.housingType || newValuesMap.housingType === HousingType.RENT
              );

            case 'hasRelativesPartRent':
              return (
                (!isChangedFieldsMap.housingType ||
                  newValuesMap.housingType === HousingType.LIVING_WITH_RELATIVE) &&
                checkBooleanPendingChangeVisibility(pendingChangeValue)
              );

            case 'monthlyRelativesPartRentAmount':
              return (
                (!isChangedFieldsMap.housingType ||
                  newValuesMap.housingType === HousingType.LIVING_WITH_RELATIVE) &&
                (!isChangedFieldsMap.hasRelativesPartRent || !!newValuesMap.hasRelativesPartRent)
              );

            case AddressField.LATITUDE:
            case AddressField.LONGITUDE:
            case 'subjectProperty':
              return false;

            default:
              return true;
          }
        };

        const condition = getCondition();

        if (condition) {
          filteredPendingChangesAcc[pendingChangeKey] = pendingChangeValue;
        }

        return filteredPendingChangesAcc;
      },
      {},
    );

    acc[borrowerId] = filteredPendingChanges;

    return acc;
  }, {});

const formatPendingChangesToForm = ({
  pendingChanges,
  form,
  entitiesSideData,
  initialEntitiesSideData,
  isPositiveRentalHistoryVisible,
}) => {
  return Object.entries(pendingChanges).reduce((acc, [borrowerId, borrowerPendingChanges]) => {
    const {
      dateOfBirth,
      alternateNames,
      declarations,
      previousAddresses,
      currentResidence,
      creditReporting,
      ...restBorrowerPendingChanges
    } = borrowerPendingChanges;
    const { militaryService, homeownershipEducationAndHousingCounseling } = declarations ?? {};

    const mergedPrevAddressesPendingChanges =
      entitiesSideData?.[borrowerId]?.previousAddresses?.reduce((acc, prevAddressData) => {
        const initialPrevAddressData = initialEntitiesSideData?.[
          borrowerId
        ]?.previousAddresses?.find(
          initialPrevAddressData => initialPrevAddressData.id === prevAddressData.id,
        );

        const historyPendingChanges = previousAddresses?.[prevAddressData.addressId] ?? {};

        const prevAddressPendingChanges = getCurrentEntityPendingChanges({
          initialValues: initialPrevAddressData ?? prevAddressData,
          currentValues: prevAddressData,
          omittedFields: previousAddressModifiedFieldsToExclude,
          actionType: Object.values(historyPendingChanges).some(
            ({ actionType }) => actionType === HistoryPendingChangesActionType.ADDED,
          )
            ? HistoryPendingChangesActionType.ADDED
            : null,
        });

        const { startOfResidence, endOfResidence, ...restPrevAddressPendingChanges } =
          prevAddressPendingChanges;

        const formattedPrevAddressPendingChanges = {
          ...restPrevAddressPendingChanges,
          ...getConditionalObject(startOfResidence, () => ({
            startOfResidence: formatDatePendingChange(startOfResidence),
          })),
          ...getConditionalObject(endOfResidence, () => ({
            endOfResidence: formatDatePendingChange(endOfResidence),
          })),
        };

        const pendingChangesToFilter = previousAddresses?.[prevAddressData.addressId]
          ? mergePendingChanges(historyPendingChanges, formattedPrevAddressPendingChanges)
          : formattedPrevAddressPendingChanges;

        acc[prevAddressData.addressId ?? prevAddressData.id] =
          filterPreviousAddressForm(pendingChangesToFilter);

        return acc;
      }, {}) ?? previousAddresses;

    acc[borrowerId] = {
      currentResidence: {
        ...currentResidence,
        ...getConditionalObject(currentResidence?.startOfResidence, () => ({
          startOfResidence: formatDatePendingChange(currentResidence?.startOfResidence),
        })),
      },
      ...getConditionalObject(dateOfBirth, () => ({
        dateOfBirth: formatDatePendingChange(dateOfBirth),
      })),
      ...getConditionalObject(alternateNames, () => ({
        alternateNames: alternateNames?.slice(
          0,
          form[borrowerId]?.alternateNames?.length ?? Infinity,
        ),
      })),
      ...getConditionalObject(declarations, () => ({
        declarations: {
          ...declarations,
          ...getConditionalObject(militaryService, () => ({
            militaryService: {
              ...militaryService,
              ...getConditionalObject(militaryService?.currentlyServingDate, () => ({
                currentlyServingDate: formatDatePendingChange(
                  militaryService?.currentlyServingDate,
                ),
              })),
            },
          })),
          ...getConditionalObject(homeownershipEducationAndHousingCounseling, () => ({
            homeownershipEducationAndHousingCounseling: {
              ...homeownershipEducationAndHousingCounseling,
              ...getConditionalObject(
                homeownershipEducationAndHousingCounseling?.dateOfCompletionHomeownershipEducation,
                () => ({
                  dateOfCompletionHomeownershipEducation: formatDatePendingChange(
                    homeownershipEducationAndHousingCounseling?.dateOfCompletionHomeownershipEducation,
                  ),
                }),
              ),
              ...getConditionalObject(
                homeownershipEducationAndHousingCounseling?.dateOfCompletionHousingCounseling,
                () => ({
                  dateOfCompletionHousingCounseling: formatDatePendingChange(
                    homeownershipEducationAndHousingCounseling?.dateOfCompletionHousingCounseling,
                  ),
                }),
              ),
            },
          })),
        },
      })),
      previousAddresses: mergedPrevAddressesPendingChanges,
      creditReporting: {
        ...creditReporting,
        ...getConditionalObject(isPositiveRentalHistoryVisible, {
          positiveRentalHistory: {
            ...creditReporting?.positiveRentalHistory,
            initialValue:
              creditReporting?.positiveRentalHistory?.initialValue === '' ||
              creditReporting?.positiveRentalHistory?.initialValue,
          },
        }),
      },
      ...restBorrowerPendingChanges,
    };

    return acc;
  }, {});
};

export const normalizePendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  data,
  form,
  isEditFlow = false,
  entitiesSideData,
  initialEntitiesSideData,
  newEntities,
  deletedEntities,
  isBorrowerFlow,
  subjectPropertyAddressId,
  isPositiveRentalHistoryVisible,
  isCaivrsNumberVisible,
}) => {
  const normalizedInitialPendingChanges = normalizeInitialPendingChanges({
    pendingChanges: initialPendingChanges,
    isBorrowerFlow,
    subjectPropertyAddressId,
  });

  const mergedPendingChanges = mergePendingChanges(
    normalizedInitialPendingChanges,
    formPendingChanges,
  );

  const filteredPendingChanges = filterPendingChanges({
    pendingChanges: mergedPendingChanges,
    passedBorrowerData: data,
    isFirstRun: true,
    isPositiveRentalHistoryVisible,
    isCaivrsNumberVisible,
  });

  const dataToReturn = isEditFlow
    ? formatPendingChangesToForm({
        pendingChanges: filteredPendingChanges,
        data,
        form,
        entitiesSideData,
        initialEntitiesSideData,
        isPositiveRentalHistoryVisible,
      })
    : filteredPendingChanges;

  return fillPendingChangesWithActionType({
    pendingChanges: dataToReturn,
    newRecords: newEntities,
    deletedRecords: deletedEntities,
  });
};
